import { COLUMN_NAME_ID } from '../../../constants';

export const CampaignColumn = Object.freeze({
  Alert: 'Alert',
  Oms: 'Oms',
  Name: COLUMN_NAME_ID,
  TrackingId: 'TrackingId',
  Brand: 'brand',
  Country: 'Country',
  OrderType: 'OrderType',
  BillingCalendar: 'BillingCalendar',
  OrderOmsLinkId: 'OrderOmsLinkId',
  TraffickerEmail: 'TraffickerEmail',
  OrderSalespersonEmail: 'OrderSalespersonEmail',
  Start: 'Start',
  End: 'End',
  Timezone: 'Timezone',
  Advertiser: 'advertiser',
  IndustryName: 'industryName',
  Agency: 'agency',
  CampaignID: 'campaignID',
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  AdAccount: 'adAccount',
  AdAccountId: 'AdAccountId',
  BrandId: 'BrandId',
  CampaignBudget: 'campaignBudget',
  MicroStrategyReport: 'MicroStrategyReport',
  Status: 'Status',
});

export const CampaignColumnMeta: Record<string, { header: string; tooltip?: string; subHeader?: string }> = {
  [CampaignColumn.Alert]: {
    header: 'Alert',
    tooltip: 'An icon in this column indicates an issue with the campaign. Hover over the icon for more information.',
  },
  [CampaignColumn.Oms]: {
    header: 'OMS',
  },
  [CampaignColumn.Name]: {
    header: 'Campaign Name',
    tooltip: 'The name of the campaign as booked in Salesforce/Operative.',
  },
  [CampaignColumn.TrackingId]: {
    header: 'Tracking ID',
    tooltip: 'External ID for the campaign shared with third-party vendors and populated in tag macros.',
  },
  [CampaignColumn.Brand]: {
    header: 'Brand',
    tooltip: 'The specific brand under the larger parent advertising company.',
  },
  [CampaignColumn.Country]: {
    header: 'Country',
    tooltip: 'The country where the campaign is eligible to serve.',
  },
  [CampaignColumn.OrderType]: {
    header: 'Order Type',
    tooltip: 'Identifies the type of order booked.',
  },
  [CampaignColumn.BillingCalendar]: {
    header: 'Billing Calendar',
    tooltip: 'Indicates whether campaign is billed using calendar month (Gregorian) or broadcast month.',
  },
  [CampaignColumn.OrderOmsLinkId]: {
    header: 'Operative Sales Order ID',
    tooltip: 'The identifier for the order in Operative.',
  },
  [CampaignColumn.TraffickerEmail]: {
    header: 'Account Manager',
    tooltip:
      'The Ad Ops Account Manager responsible for managing campaign setup and delivery fulfillment. This value is pushed from Operative.One.',
  },
  [CampaignColumn.OrderSalespersonEmail]: {
    header: 'Account Executive',
    tooltip: 'The primary salesperson on the campaign.',
  },
  [CampaignColumn.Start]: {
    header: 'Start Date',
    subHeader: 'mm/dd/yyyy 00:00 (24 hour clock)',
    tooltip: 'The campaign start date derived from the earliest line item start date.',
  },
  [CampaignColumn.End]: {
    header: 'End Date',
    subHeader: 'mm/dd/yyyy 00:00 (24 hour clock)',
    tooltip: 'The campaign end date derived from the latest line item end date.',
  },
  [CampaignColumn.Timezone]: {
    header: 'Time Zone',
    tooltip: 'The time zone for the campaign as defined in order.',
  },
  [CampaignColumn.Advertiser]: {
    header: 'Advertiser',
    tooltip: 'The name of the advertising company the campaign belongs to.',
  },
  [CampaignColumn.IndustryName]: {
    header: 'Industry',
    tooltip: 'The industry category assigned to the brand.',
  },
  [CampaignColumn.Agency]: {
    header: 'Agency',
    tooltip: 'The buying agency contracting the campaign.',
  },
  [CampaignColumn.CampaignID]: {
    header: 'Campaign ID',
    tooltip: 'UUID used for troubleshooting and internal communications. Should not be shared externally.',
  },
  [CampaignColumn.CreatedAt]: {
    header: 'Created Date',
    tooltip: 'The date the campaign was initially pushed from Operative.One to Mission Control.',
  },
  [CampaignColumn.UpdatedAt]: {
    header: 'Updated Date',
    tooltip: 'The date the campaign was subsequently pushed from Operative.One to Mission Control.',
  },
  [CampaignColumn.AdAccount]: {
    header: 'Ad Account',
    tooltip: 'The advertiser/agency ad account as designated upstream in the Account Manager backend service.',
  },
  [CampaignColumn.AdAccountId]: {
    header: 'Ad Account Id',
    tooltip: 'The ID associated with the ad account in the Account Manager backend service.',
  },
  [CampaignColumn.BrandId]: {
    header: 'Brand Id',
    tooltip: 'The identifier associated with the advertiser brand as designated upstream in Customer Manager.',
  },
  [CampaignColumn.CampaignBudget]: {
    header: 'Campaign Budget',
    tooltip: 'The campaign booked revenue derived from total line item booked revenue.',
  },
  [CampaignColumn.MicroStrategyReport]: {
    header: 'MicroStrategy Report',
  },
  [CampaignColumn.Status]: {
    header: 'Campaign Status',
    tooltip: 'Indicates the status of the campaign as derived from the line items within the campaign.',
  },
};
